import { MouseEvent as ReactMouseEvent, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { ClickAwayListener, IconButton, Popper, alpha } from '@mui/material';

import styled from 'styled-components';

import { ROUTES, getPropertyPrimaryPhoto } from '@arrived/common';
import { CONFIG } from '@arrived/config';
import { Share } from '@arrived/images';
import { Offering } from '@arrived/models';

import { getPopperButtonConfigs } from './SocialShareButtonConfigs';
import { SocialShareButtons } from './SocialShareButtons';

interface SocialSharePopperProps {
  edge?: 'start' | 'end' | false;
  offering: Offering;
}

const StyledPopper = styled(Popper)`
  &.social-share-popper {
    z-index: ${({ theme }) => theme.zIndex.tooltip};
    background-color: ${({ theme }) => alpha(theme.palette.darkened.steel, 0.75)};
    // The buttons are set to inherit line-height, so we remove whitespace here so we don't have whitespace below our buttons
    line-height: 0;

    // Note: Some values in here might need an !important because the library is silly and does a "reset" on the button as inline css
    .react-share__ShareButton {
      padding: ${(props) => props.theme.spacing(1, 0.5)} !important;

      &:first-child {
        padding-left: ${(props) => props.theme.spacing(1)} !important;
      }

      &:last-child {
        padding-right: ${(props) => props.theme.spacing(1)} !important;
      }

      &:hover {
        background-color: ${({ theme }) => alpha(theme.palette.neutrals.milk, 0.75)} !important;
      }
    }
  }
`;

export const SocialSharePopper = ({ edge, offering }: SocialSharePopperProps) => {
  const property = offering.properties[0];

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleOnClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    const isAnchorElementUnset = anchorElement === null;

    setAnchorElement(isAnchorElementUnset ? target : null);
    setIsOpen(isAnchorElementUnset);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const path = event.composedPath();

    // If trying to click the share button on mobile, this fires... Why? Who knows... this fixes that
    if (wrapperRef.current && !path.includes(wrapperRef.current)) {
      setIsOpen(false);
      setAnchorElement(null);
    }
  };

  const title = intl.formatMessage({ id: 'social-share-buttons-title', defaultMessage: 'Arrived Homes' });
  const text = intl.formatMessage({
    id: 'social-share-buttons-text',
    defaultMessage: 'Buy shares of rental properties, earn passive income, and let Arrived take care of the rest.',
  });
  const mainHashtag = intl.formatMessage({
    id: 'social-share-buttons-main-hashtag',
    defaultMessage: 'ArrivedHomes',
  });
  const imageUrl = getPropertyPrimaryPhoto(property);
  const buttonConfig = getPopperButtonConfigs(title, text, mainHashtag, imageUrl);

  return (
    <div ref={wrapperRef} className="social-share-popper-wrapper">
      <IconButton
        onClick={handleOnClick}
        edge={typeof edge !== 'undefined' ? edge : 'end'}
        aria-label={intl.formatMessage({ id: 'social-share-popper-label', defaultMessage: 'Share' })}
        size="large"
      >
        <Share />
      </IconButton>
      <StyledPopper
        anchorEl={anchorElement}
        container={() => containerRef.current}
        open={isOpen}
        placement="bottom-end"
        className="social-share-popper"
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <SocialShareButtons
              buttonConfig={buttonConfig}
              iconSize={32}
              url={`${CONFIG.socialShareUrlRoot}${generatePath(ROUTES.properties.propertyDetails, {
                idOrShortName: offering.shortName,
              })}`}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </div>
  );
};
